import "~/styles/globals.css"
import "~/utils/highcharts-config" // Configure Highcharts globally before any charts are rendered
import * as Sentry from "@sentry/react"
import { StrictMode, useEffect } from "react"
import ReactDOM from "react-dom/client"
import { RouterProvider } from "@tanstack/react-router"
import { IconContext } from "@phosphor-icons/react"
import { TooltipProvider } from "@radix-ui/react-tooltip"
import { withLDProvider } from "launchdarkly-react-client-sdk"

import { SkuStoreProvider } from "~/providers/sku-store-provider"
import { DateRangeStoreProvider } from "~/providers/dateRangeStoreProvider"
import { HotkeysProvider } from "@tanstack/react-hotkeys"
import {
  amplitudeAPIKey,
  appTitleName,
  isAmplitudeEnabled,
  isDemo,
  isLocal,
  isProduction,
  launchDarklyClientSideID,
} from "~/utils/environment"
import { TrpcProvider } from "~/providers/TrpcProvider"
import { api } from "~/utils/api"
import { UpdatesProvider } from "~/providers/UpdatesProvider"
import { UserProvider } from "~/providers/userProvider"
import { ThemeProvider } from "next-themes"
import { createRouter } from "./router"
import { Auth0Provider, Auth0ProviderOptions, useAuth0 } from "@auth0/auth0-react"
import { TestAuth0Provider } from "~/providers/TestAuth0Provider"
import { isMockAuth } from "~/utils/mockAuth"
import { LicenseManager } from "ag-grid-enterprise"
import useTitle from "~/hooks/useTitle"
import * as amplitude from "@amplitude/analytics-browser"
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser"
import GenericError from "~/components/reusable/errors/GenericError"
import { IntercomProviderWrapper } from "~/providers/intercom-provider"
import { SENTRY_DSN } from "~/utils/sentry-config"
import {
  isIntentionalSentryTest,
  isStaleChunkImportFailure,
  isThirdPartyStorageNoise,
  isTransientTransportNoise,
} from "~/utils/sentryErrorFilter"

const CHUNK_RELOAD_KEY = "hakio:chunk-reload"
window.addEventListener("vite:preloadError", () => {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
  sessionStorage.setItem(CHUNK_RELOAD_KEY, "1")
  // No event.preventDefault() here: preventing makes Vite resolve the failed import()
  // with undefined, which TanStack's lazyRouteComponent dereferences before the reload
  // lands (CORE-PR). Rethrowing lets the import reject and TanStack reload-guard it too.
  window.location.reload()
})
window.addEventListener("load", () => sessionStorage.removeItem(CHUNK_RELOAD_KEY))

// HAK-2306: only load Amplitude (SDK init + session replay) when the production SDK key is
// in use. In dev/staging/demo/local it stays fully disabled to avoid inflating active-user
// counts and polluting analytics.
if (isAmplitudeEnabled()) {
  // Routed through our own origin so tracking-prevention extensions don't drop analytics —
  // see server/handlers/amplitude-proxy.ts. Diagnostics has no URL override, so it's off.
  amplitude.init(amplitudeAPIKey(), {
    serverZone: "EU",
    serverUrl: "/api/amplitude/httpapi",
    remoteConfig: {
      serverUrl: "/api/amplitude/config",
    },
    enableDiagnostics: false,
    autocapture: {
      elementInteractions: true,
      pageViews: false,
    },
  })

  amplitude.add(
    sessionReplayPlugin({
      sampleRate: 1,
      // Absolute: replay uploads fetch from inside a blob-URL worker, where a root-relative
      // path fails to parse.
      trackServerUrl: `${window.location.origin}/api/amplitude/sr-track`,
      configServerUrl: "/api/amplitude/config",
    }),
  )
}

LicenseManager.setLicenseKey(import.meta.env.VITE_HAKIO_AG_GRID_LICENSE_KEY ?? "")

const router = createRouter()

const isFeatureBranchSentry = import.meta.env.VITE_SENTRY_ENABLED === "true"

if (!isLocal() && (isProduction() || isDemo() || isFeatureBranchSentry)) {
  console.info("Initializing Sentry")
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: isFeatureBranchSentry ? "development" : import.meta.env.VITE_RUNTIME_ENVIRONMENT,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["hakio-core"],
        behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
      }),
    ],
    tracesSampleRate: 0.1,
    traceLifecycle: "static",
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      httpBodies: [],
      urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      stackFrameVariables: false,
    },
    tunnel: "/api/tunnel",
    beforeSend(event, hint) {
      const err = hint?.originalException
      // Benign browser-managed aborts/timeouts (IDB transactions cancelled or timed
      // out by the browser, fetch aborts, etc.) surface as unhandled rejections from
      // third-party SDKs like Amplitude session replay. They are noise, not bugs.
      // AbortError (code 20) and TimeoutError (code 23, "Transaction timed out due to
      // inactivity") both come from the replay SDK's IndexedDB usage under load.
      if (
        err instanceof DOMException &&
        (err.name === "AbortError" || err.name === "TimeoutError")
      ) {
        return null
      }
      if (isThirdPartyStorageNoise(err)) return null
      // Transient tRPC transport failures — handled by the app, nothing actionable.
      if (isTransientTransportNoise(err)) return null
      // Deliberate "Test Sentry" events (HAK-2136): keep delivering them so the integration
      // test still works, but tag + fingerprint + demote so they never enter bug triage.
      if (isIntentionalSentryTest(err)) {
        event.tags = { ...event.tags, test_event: true }
        event.fingerprint = ["sentry-test-error"]
        event.level = "info"
        return event
      }
      // The asset hash changes every deploy, so default grouping mints a new issue per build.
      if (isStaleChunkImportFailure(err)) event.fingerprint = ["stale-chunk-import"]
      return event
    },
  })
}

const AppContent = () => {
  const auth = useAuth0()
  const trpcUtils = api.useUtils()
  const { user } = auth

  useEffect(() => {
    if (!user) return

    // Jam SDK auto-initializes on import and crashes on Chrome Mobile (no SharedWorker).
    // Jam is a desktop screen-recording tool, so skipping it on touch devices is intentional.
    const isCoarsePointer =
      typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches
    if (isCoarsePointer) return

    void import("@jam.dev/sdk").then(({ jam }) => {
      jam.metadata(() => ({
        userId: user.sub,
        orgId: user.org_id,
        orgName: user.org_name,
      }))
    })
  }, [user])

  return (
    <IconContext.Provider
      value={{
        size: 16,
        weight: "bold",
        format: "outline",
      }}
    >
      <Sentry.ErrorBoundary fallback={<GenericError />}>
        <RouterProvider router={router} context={{ auth, trpcUtils }} />
      </Sentry.ErrorBoundary>
    </IconContext.Provider>
  )
}

const BaseApp = () => {
  useTitle(appTitleName())

  const isTestMode = isMockAuth()

  const AuthProvider = isTestMode ? TestAuth0Provider : Auth0Provider
  const authProps = isTestMode
    ? ({} as Auth0ProviderOptions)
    : {
        domain: "hakio.eu.auth0.com",
        clientId: "BZJZ672aMKWYgToHKrxqlVKj3CTvLehK",
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: "https://hakio.eu.auth0.com/api/v2/",
          scope: "openid profile email offline_access",
        },
        useRefreshTokens: true,
        cacheLocation: "localstorage" as const,
      }

  return (
    <ThemeProvider defaultTheme="system" attribute={["data-theme", "class"]}>
      <TooltipProvider delayDuration={100}>
        <AuthProvider {...authProps}>
          <TrpcProvider>
            <UserProvider>
              <UpdatesProvider />
              <IntercomProviderWrapper>
                <SkuStoreProvider>
                  <DateRangeStoreProvider>
                    <HotkeysProvider>
                      <AppContent />
                    </HotkeysProvider>
                  </DateRangeStoreProvider>
                </SkuStoreProvider>
              </IntercomProviderWrapper>
            </UserProvider>
          </TrpcProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  )
}

const AppWithProviders = withLDProvider({
  clientSideID: launchDarklyClientSideID(),
  deferInitialization: false,
})(BaseApp)

const rootElement = document.getElementById("root")!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack)
    }),
    // Callback called when React catches an error in an ErrorBoundary
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors
    onRecoverableError: Sentry.reactErrorHandler(),
  })

  root.render(
    <StrictMode>
      <AppWithProviders />
    </StrictMode>,
  )
}
